import { useEffect, useState } from 'react'
import { Alert, Form } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AdminPageHero } from '@/components/common/AdminPageHero'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { orderDefaults, parseStoredForm, type OrderForm } from '@/components/forms/formModel'
import { collectOrderJson, OrderFormPages } from '@/components/forms/OrderFieldInputs'
import { useAuth } from '@/context/AuthContext'
import { forThisBusiness } from '@/helpers/businessName'
import { readError } from '@/services/apiClient'
import { loadCustomer, loadForm, loadOrderForms, placeOrder, type CustomerDetail, type OrderFormOption } from '@/services/customerService'

export function CreateOrderScreen() {
  const { customerId = '' } = useParams()
  const navigate = useNavigate()
  const { session } = useAuth()
  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [forms, setForms] = useState<OrderFormOption[]>([])
  const [formId, setFormId] = useState('')
  const [form, setForm] = useState<OrderForm | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([loadCustomer(customerId), loadOrderForms()])
      .then(([row, options]) => {
        if (cancelled) return
        setCustomer(row)
        setForms(options.filter((item) => item.hasPublishedVersion))
      })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [customerId])

  useEffect(() => {
    if (!formId) {
      setForm(null)
      return
    }
    let cancelled = false
    setForm(null)
    setError(null)
    loadForm(Number(formId))
      .then((row) => {
        if (cancelled) return
        const published = parseStoredForm(row.publishedFormJson, { id: String(row.id), name: row.formName })
        setForm(published ? { ...published, publishedVersionId: row.currentVersionId } : null)
        if (!published) setError('This form has not been published yet.')
      })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [formId])

  async function submit(element: HTMLFormElement) {
    if (customer?.onStop || customer?.deleted) { setError('Forms cannot be submitted for a stopped or deleted customer.'); return }
    const orderJson = collectOrderJson(element)
    setBusy(true)
    setError(null)
    try {
      await placeOrder({ formId: Number(formId), formVersionId: form?.publishedVersionId ?? 0, customerId, orderJson })
      navigate('/')
    } catch (caught) {
      setError(readError(caught))
      setBusy(false)
    }
  }

  return (
    <>
      <AdminPageHero eyebrow="Workspace / CRM" title="New submission" summary={customer ? customer.name : 'Loading customer'} />
      {!customer && !error && <LoadingSpinner text="Loading customer" size="3x" />}
      {error && <Alert variant="danger">{error}</Alert>}
      {customer && (
        <section className="admin-panel form-panel">
          <p>{[customer.accountNumber, customer.address1, customer.city, customer.postcode].filter(Boolean).join(' · ')}</p>
          <Form.Group className="mb-3">
            <Form.Label>Form</Form.Label>
            <Form.Select required disabled={busy} value={formId} onChange={(event) => setFormId(event.target.value)}>
              <option value="">Select form</option>
              {forms.map((item) => <option key={item.id} value={item.id}>{item.formName}</option>)}
            </Form.Select>
          </Form.Group>
          {!forms.length && <Alert variant="info">No published forms are available {forThisBusiness(session)} yet.</Alert>}
          {(customer.onStop || customer.deleted) && <Alert variant="warning">Forms cannot be submitted for a stopped or deleted customer.</Alert>}
          {form && !customer.onStop && !customer.deleted && (
            <OrderFormPages
              key={form.id}
              form={form}
              cataloguePricing={{ formId: Number(formId), formVersionId: form.publishedVersionId ?? 0, customerId, resourceId: session?.billingResourceId, accountId: session?.billingAccountId }}
              busy={busy}
              defaults={orderDefaults(customer, session?.fullName)}
              submitLabel="Submit"
              onSubmit={(element) => void submit(element)}
            />
          )}
          <Link className={`btn btn-outline-secondary${busy ? ' disabled' : ''}`} aria-disabled={busy} tabIndex={busy ? -1 : undefined} onClick={event => { if (busy) event.preventDefault() }} to="/">Cancel</Link>
        </section>
      )}
    </>
  )
}


